import {
  ISupportingActor,
  SupportingActorStaticUser,
  SupportingActorType
} from '@dadohr/shared/domainTypes/experience';
import { CustomInputLabel } from '../../../../../../../components/CustomInputLabel';
import { HalfWidthField } from '../../ActorDialogParts';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import type {
  ActorTypeFieldsProps,
  ActorTypeForm,
  StaticUserActorState
} from '..';
import { AutocompleteUserId } from '../AutocompleteUserId';

// Shared by the two choices stored as a static user: "The same person for all
// participants" and "Based on rules".

export const staticUserFromActor = (
  actor: ISupportingActor,
  defaultColor: string
): StaticUserActorState => {
  const a = actor as SupportingActorStaticUser;
  return {
    name: a.name || '',
    defaultUserId: a.defaultUserId,
    ruleBasedUserIds: a.ruleBasedUserIds,
    // A rule-based actor saved without a default user is left unassigned when
    // no option matches.
    defaultUnassigned: !!a.ruleBasedUserIds.length && !a.defaultUserId,
    type: SupportingActorType.STATIC_USER,
    color: a.color || defaultColor,
    ignoreWhenMissing: !!a.ignoreWhenMissing
  };
};

export const staticUserToActor = (
  { defaultUnassigned, ...state }: StaticUserActorState,
  key: string
): ISupportingActor => ({
  type: SupportingActorType.STATIC_USER,
  name: state.name,
  key,
  defaultUserId: state.defaultUserId,
  ruleBasedUserIds: state.ruleBasedUserIds,
  color: state.color,
  ignoreWhenMissing: state.ignoreWhenMissing
});

const SamePersonActorFields = ({
  state,
  onChange,
  disabled,
  showValidation,
  orgId
}: ActorTypeFieldsProps<StaticUserActorState>) => {
  const { t } = useUnsafeTranslation();
  return (
    <HalfWidthField>
      <CustomInputLabel fullWidth label={t('User')}>
        <AutocompleteUserId
          disabled={disabled}
          error={showValidation && !state.defaultUserId}
          helperText={
            showValidation && !state.defaultUserId
              ? t('Please select a user')
              : undefined
          }
          label=""
          placeholder={t('Select user')}
          orgId={orgId}
          value={state.defaultUserId}
          onChange={(nextUserId) =>
            onChange({ ...state, defaultUserId: nextUserId })
          }
        />
      </CustomInputLabel>
    </HalfWidthField>
  );
};

/** "The same person for all participants": a static user without rules. */
export const samePersonActorType: ActorTypeForm<StaticUserActorState> = {
  option: SupportingActorType.STATIC_USER,
  label: (t) => t(`The same person for all participants`),
  matches: (actor) =>
    actor.type === SupportingActorType.STATIC_USER &&
    !actor.ruleBasedUserIds.length,
  fromActor: staticUserFromActor,
  create: (base) => ({
    name: base.name,
    type: SupportingActorType.STATIC_USER,
    defaultUserId: '',
    ruleBasedUserIds: [],
    defaultUnassigned: false,
    color: base.color,
    ignoreWhenMissing: base.ignoreWhenMissing
  }),
  isComplete: (state) => !!state.defaultUserId,
  toActor: staticUserToActor,
  Fields: SamePersonActorFields
};
